// src/api/membresias.js
import { apiFetch } from './index.js';

// ============================================================
// HELPERS
// ============================================================

const obtenerIdUsuarioActual = () => {
  try {
    const userStr = localStorage.getItem('user') || localStorage.getItem('user_data');
    if (userStr) {
      const user = JSON.parse(userStr);
      const id = user?.idUsuario ?? user?.id ?? user?.id_usuario ?? user?.IdUsuario;
      if (id && !isNaN(parseInt(id))) {
        console.log('👤 ID usuario obtenido:', id);
        return parseInt(id);
      }
    }
  } catch (e) {
    console.warn('⚠️ Error leyendo usuario del localStorage:', e);
  }
  console.log('⚠️ Usando fallback idUsuario = 1');
  return 1;
};

// ============================================================
// TIPOS DE MEMBRESÍA
// ============================================================

export const obtenerTiposMembresia = async () => {
  try {
    console.log('📋 Obteniendo tipos de membresía...');
    const response = await apiFetch('/Membresia/tipos');
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tipos obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo tipos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerTipoMembresiaPorId = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    const response = await apiFetch(`/Membresia/tipos/${id}`);
    return { success: true, data: response, message: 'Tipo obtenido' };
  } catch (error) {
    console.error(`❌ Error obteniendo tipo ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const crearTipoMembresia = async (data) => {
  try {
    const required = ['codigo', 'nombre', 'duracion_dias'];
    const missing = required.filter(f => data[f] === undefined || data[f] === null || data[f] === '');
    if (missing.length > 0) throw new Error(`Campos requeridos: ${missing.join(', ')}`);

    console.log('➕ Creando tipo de membresía:', data);
    const response = await apiFetch('/Membresia/tipos', {
      method: 'POST',
      body: JSON.stringify({
        codigo: data.codigo.toUpperCase(),
        nombre: data.nombre,
        descripcion: data.descripcion || null,
        duracion_dias: parseInt(data.duracion_dias),
        precio_usd: parseFloat(data.precio_usd) || 0,
        precio_bs: parseFloat(data.precio_bs) || 0,
        tiene_acceso_torniquete: data.tiene_acceso_torniquete !== false,
        limite_accesos_dia: data.limite_accesos_dia ? parseInt(data.limite_accesos_dia) : null,
        horario_inicio: data.horario_inicio || null,
        horario_fin: data.horario_fin || null,
        estado: data.estado || 'activo'
      })
    });
    return { success: true, data: response.data || response, message: 'Tipo creado exitosamente' };
  } catch (error) {
    console.error('❌ Error creando tipo:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const actualizarTipoMembresia = async (id, data) => {
  try {
    if (!id) throw new Error('ID es requerido');
    console.log(`✏️ Actualizando tipo ${id}`);
    const response = await apiFetch(`/Membresia/tipos/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        codigo: data.codigo?.toUpperCase(),
        nombre: data.nombre,
        descripcion: data.descripcion || null,
        duracion_dias: parseInt(data.duracion_dias),
        precio_usd: parseFloat(data.precio_usd) || 0,
        precio_bs: parseFloat(data.precio_bs) || 0,
        tiene_acceso_torniquete: data.tiene_acceso_torniquete !== false,
        limite_accesos_dia: data.limite_accesos_dia ? parseInt(data.limite_accesos_dia) : null,
        horario_inicio: data.horario_inicio || null,
        horario_fin: data.horario_fin || null,
        estado: data.estado || 'activo'
      })
    });
    return { success: true, data: response.data || response, message: 'Tipo actualizado' };
  } catch (error) {
    console.error(`❌ Error actualizando tipo ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const eliminarTipoMembresia = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    console.log(`🗑️ Eliminando tipo ${id}`);
    const response = await apiFetch(`/Membresia/tipos/${id}`, { method: 'DELETE' });
    return { success: true, data: response, message: 'Tipo desactivado' };
  } catch (error) {
    console.error(`❌ Error eliminando tipo ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

// ============================================================
// MEMBRESÍAS (SOCIOS)
// ============================================================

export const obtenerMembresias = async (filtros = {}) => {
  try {
    console.log('👥 Obteniendo membresías...', filtros);

    if (filtros.documento || filtros.nombre || filtros.estado || filtros.idTipoMembresia) {
      const response = await apiFetch('/Membresia/search', {
        method: 'POST',
        body: JSON.stringify({
          documento: filtros.documento || null,
          nombre: filtros.nombre || null,
          estado: filtros.estado || null,
          idTipoMembresia: filtros.idTipoMembresia ? parseInt(filtros.idTipoMembresia) : null,
          fecha_desde: filtros.fecha_desde || null,
          fecha_hasta: filtros.fecha_hasta || null
        })
      });
      return { success: true, data: response, count: Array.isArray(response) ? response.length : 0 };
    }

    const response = await apiFetch('/Membresia');
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Membresías obtenidas'
    };
  } catch (error) {
    console.error('❌ Error obteniendo membresías:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerMembresiaPorId = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    const response = await apiFetch(`/Membresia/${id}`);
    return { success: true, data: response, message: 'Membresía obtenida' };
  } catch (error) {
    console.error(`❌ Error obteniendo membresía ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerMembresiasActivas = async () => {
  try {
    const response = await apiFetch('/Membresia/activas');
    return { success: true, data: response, count: Array.isArray(response) ? response.length : 0 };
  } catch (error) {
    console.error('❌ Error obteniendo membresías activas:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerMembresiasPorVencer = async (dias = 7) => {
  try {
    const response = await apiFetch(`/Membresia/por-vencer?dias=${dias}`);
    return { success: true, data: response, count: Array.isArray(response) ? response.length : 0 };
  } catch (error) {
    console.error('❌ Error obteniendo membresías por vencer:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const crearMembresia = async (data) => {
  try {
    if (!data.idTipoMembresia) throw new Error('Tipo de membresía es requerido');
    if (!data.idCliente && !data.idUsuario) throw new Error('Cliente o usuario es requerido');
    if (!data.fecha_inicio) throw new Error('Fecha de inicio es requerida');

    const idUsuarioRegistro = data.idUsuarioRegistro || obtenerIdUsuarioActual();

    console.log('➕ Creando membresía:', { ...data, idUsuarioRegistro });

    const response = await apiFetch('/Membresia', {
      method: 'POST',
      body: JSON.stringify({
        idUsuario: data.idUsuario || null,
        idCliente: data.idCliente || null,
        idTipoMembresia: parseInt(data.idTipoMembresia),
        idVenta: data.idVenta || null,
        fecha_inicio: data.fecha_inicio,
        fecha_fin: data.fecha_fin || null,
        precio_usd: data.precio_usd ? parseFloat(data.precio_usd) : null,
        precio_bs: data.precio_bs ? parseFloat(data.precio_bs) : null,
        foto_socio: data.foto_socio || null,
        observaciones: data.observaciones || null,
        idUsuarioRegistro: parseInt(idUsuarioRegistro)
      })
    });
    return { success: true, data: response.data || response, message: 'Membresía creada' };
  } catch (error) {
    console.error('❌ Error creando membresía:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const actualizarMembresia = async (id, data) => {
  try {
    if (!id) throw new Error('ID es requerido');
    console.log('Actualizando membresia ' + id);
    const idTipo = data.idTipoMembresia ?? data.IdTipoMembresia;
    const response = await apiFetch('/Membresia/' + id, {
      method: 'PUT',
      body: JSON.stringify({
        idTipoMembresia: idTipo ? parseInt(idTipo) : undefined,
        fecha_inicio: data.fecha_inicio ?? data.FechaInicio,
        fecha_fin: data.fecha_fin ?? data.FechaFin,
        precio_usd: parseFloat(data.precio_usd ?? data.PrecioUsd) || 0,
        precio_bs: parseFloat(data.precio_bs ?? data.PrecioBs) || 0,
        saldo_usd: parseFloat(data.saldo_usd ?? data.SaldoUsd) || 0,
        saldo_bs: parseFloat(data.saldo_bs ?? data.SaldoBs) || 0,
        estado: data.estado ?? data.Estado,
        acceso_habilitado: data.acceso_habilitado ?? data.AccesoHabilitado,
        foto_socio: data.foto_socio ?? data.FotoSocio ?? null,
        observaciones: data.observaciones ?? data.Observaciones ?? null
      })
    });
    return { success: true, data: response.data || response, message: 'Membresia actualizada' };
  } catch (error) {
    console.error('Error actualizando membresia ' + id + ':', error);
    return { success: false, error: error.message, data: null };
  }
};

export const eliminarMembresia = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    const response = await apiFetch(`/Membresia/${id}`, { method: 'DELETE' });
    return { success: true, data: response, message: 'Membresía cancelada' };
  } catch (error) {
    console.error(`❌ Error eliminando membresía ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const renovarMembresia = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    const idUsuario = obtenerIdUsuarioActual();
    const response = await apiFetch(`/Membresia/${id}/renovar?idUsuarioRegistro=${idUsuario}`, {
      method: 'POST'
    });
    return { success: true, data: response, message: 'Membresía renovada' };
  } catch (error) {
    console.error(`❌ Error renovando membresía ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

// ============================================================
// 🎭 SINCRONIZACIÓN CON DISPOSITIVO FACIAL FSO701
// ============================================================

/**
 * Registra la foto del socio en el FSO701 automáticamente.
 */
export const registrarRostroDispositivo = async (idMembresia, fotoBase64) => {
  try {
    if (!idMembresia) throw new Error('ID de membresía requerido');
    if (!fotoBase64) throw new Error('Foto requerida');

    console.log(`📸 Registrando rostro para membresía ${idMembresia}...`);

    const response = await apiFetch(`/Membresia/${idMembresia}/registrar-rostro`, {
      method: 'POST',
      body: JSON.stringify({ FotoBase64: fotoBase64 })
    });

    console.log('✅ Respuesta del servidor:', response);
    return {
      success: response.success !== false,
      data: response,
      message: response.mensaje || 'Rostro registrado'
    };
  } catch (error) {
    console.error('❌ Error registrando rostro:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * ⭐ Verifica el estado del dispositivo FSO701.
 */
export const verificarFSO701 = async () => {
  try {
    const response = await apiFetch('/device/status');
    return { success: true, data: response };
  } catch (error) {
    console.error('❌ Error verificando FSO701:', error);
    return { success: false, error: error.message, data: null };
  }
};

// ============================================================
// ⭐ NUEVO: BIOMÉTRICO (Lector FS88H)
// ============================================================

/**
 * Estado del lector biométrico FS88H.
 */
export const estadoBiometrico = async () => {
  try {
    const response = await apiFetch('/huella/estado');
    return {
      success: true,
      data: { conectado: response?.conectado ?? false },
      ...response
    };
  } catch (error) {
    console.error('❌ Error estado biométrico:', error);
    return { success: false, error: error.message, data: { conectado: false } };
  }
};

/**
 * Captura una huella del lector FS88H (para registro de nuevo socio).
 */
export const capturarHuella = async (timeoutMs = 15000) => {
  try {
    const response = await apiFetch('/huella/capturar', {
      method: 'POST',
      body: JSON.stringify({ timeoutMs })
    });
    return {
      success: response?.success ?? response?.exito ?? false,
      data: response?.data,
      error: response?.error ?? response?.mensaje,
      ...response
    };
  } catch (error) {
    console.error('❌ Error capturando huella:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Guarda la huella de un usuario/cliente en la BD.
 */
export const guardarHuella = async (idUsuario, template, formato = 'SourceAFIS') => {
  try {
    const response = await apiFetch('/huella/registrar-huella', {
      method: 'POST',
      body: JSON.stringify({
        idUsuario,
        templateData: template
      })
    });
    return {
      success: response?.exito ?? response?.success ?? false,
      mensaje: response?.mensaje,
      error: response?.mensaje ?? response?.error,
      ...response
    };
  } catch (error) {
    console.error('❌ Error guardando huella:', error);
    return { success: false, error: error.message };
  }
};

// ============================================================
// ACCESO (validación manual - por si acaso)
// ============================================================

export const validarAccesoPorCedula = async (cedula, idSede = null, idDispositivo = null) => {
  try {
    if (!cedula) throw new Error('Cédula es requerida');
    try {
      const resHuella = await apiFetch('/Huella/identificar-cedula', {
        method: 'POST',
        body: JSON.stringify({ cedula, idSede, idDispositivo })
      });
      return { success: true, ...resHuella, data: resHuella };
    } catch {
      const response = await apiFetch('/Membresia/acceso/cedula', {
        method: 'POST',
        body: JSON.stringify({ cedula, idSede, idDispositivo })
      });
      return { success: true, ...response, data: response };
    }
  } catch (error) {
    console.error('❌ Error validando acceso por cédula:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const validarAccesoPorHuella = async (idUsuario, idSede = null, idDispositivo = null) => {
  try {
    if (!idUsuario) throw new Error('ID de usuario es requerido');
    const response = await apiFetch('/Membresia/acceso/huella', {
      method: 'POST',
      body: JSON.stringify({ idUsuario, idSede, idDispositivo })
    });
    return { success: true, data: response };
  } catch (error) {
    console.error('❌ Error validando acceso por huella:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const validarAccesoPorRostro = async (idUsuario, idSede = null, idDispositivo = null) => {
  try {
    if (!idUsuario) throw new Error('ID de usuario es requerido');
    const response = await apiFetch('/Membresia/acceso/rostro', {
      method: 'POST',
      body: JSON.stringify({ idUsuario, idSede, idDispositivo })
    });
    return { success: true, data: response };
  } catch (error) {
    console.error('❌ Error validando acceso por rostro:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerAccesosHoy = async () => {
  try {
    const response = await apiFetch('/Membresia/accesos/hoy');
    return { success: true, data: response, count: Array.isArray(response) ? response.length : 0 };
  } catch (error) {
    console.error('❌ Error obteniendo accesos hoy:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerAccesos = async () => {
  try {
    const response = await apiFetch('/Membresia/accesos');
    return { success: true, data: response, count: Array.isArray(response) ? response.length : 0 };
  } catch (error) {
    console.error('❌ Error obteniendo accesos:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

// ============================================================
// ESTADÍSTICAS
// ============================================================

export const obtenerEstadisticasGym = async () => {
  try {
    const response = await apiFetch('/Membresia/estadisticas');
    return { success: true, data: response };
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas:', error);
    return { success: false, error: error.message, data: null };
  }
};

// ============================================================
// HELPERS
// ============================================================

export const formatearFechaMembresia = (fechaStr) => {
  if (!fechaStr) return 'N/A';
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return fechaStr; }
};

export const calcularDiasRestantes = (fechaFin) => {
  if (!fechaFin) return 0;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fin = new Date(fechaFin);
  fin.setHours(0, 0, 0, 0);
  return Math.ceil((fin - hoy) / (1000 * 60 * 60 * 24));
};

export default {
  // Tipos
  obtenerTiposMembresia,
  obtenerTipoMembresiaPorId,
  crearTipoMembresia,
  actualizarTipoMembresia,
  eliminarTipoMembresia,
  // Membresías
  obtenerMembresias,
  obtenerMembresiaPorId,
  obtenerMembresiasActivas,
  obtenerMembresiasPorVencer,
  crearMembresia,
  actualizarMembresia,
  eliminarMembresia,
  renovarMembresia,
  // 🎭 Registro facial
  registrarRostroDispositivo,
  verificarFSO701,
  // ⭐ Biometrico
  estadoBiometrico,
  capturarHuella,
  guardarHuella,
  // Acceso
  validarAccesoPorCedula,
  validarAccesoPorHuella,
  validarAccesoPorRostro,
  obtenerAccesosHoy,
  obtenerAccesos,
  // Estadísticas
  obtenerEstadisticasGym,
  // Helpers
  formatearFechaMembresia,
  calcularDiasRestantes
};